import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ColumnDef } from "@tanstack/react-table";
import { PartnerColumn, PartnerData, PartnerStatus } from "./types";
import { getPartnerTypeLabels, partnerStatusColors } from "./helpers";
import { getEnumTKey } from "@/utils/strings";
import { createEnumSortingFn } from "@/utils/enums";
import { simpleDate, toCalendarDate } from "@/utils/datetime";
import { StatusBadge } from "@/components/ui/status-badge";
import TableNameCell from "@/components/controls/TableNameCell";
import MultiSelectBadgesCell from "@/components/controls/DataTable/MultiSelectBadgesCell";
import { EntityType } from "@/services/hooks/entities/useEntityType";
import { ColumnData } from "@/entities/dataTables/types";
import {
  mapSelectedCustomColumnsToTableColumns,
  parseMultiSelectValue
} from "@/entities/dataTables/helpers";
import { APIFieldItem } from "@/pages/settings/types";
import { getCountryDisplayName } from "@/utils/countries";
import { getCurrentLocale } from "@/utils/locale";

const HARDCODED_PARTNER_COLUMN_IDS = new Set<string>(
  Object.values(PartnerColumn)
);

interface UsePartnerColumnsArgs {
  partnersDataMap: Record<string, PartnerData>;
  selectedColumns: ColumnData[];
  customFields: APIFieldItem[];
  isFieldVisible: (key: string) => boolean;
  fieldsLoaded: boolean;
}

export const usePartnerColumns = ({
  partnersDataMap,
  selectedColumns,
  customFields,
  isFieldVisible,
  fieldsLoaded
}: UsePartnerColumnsArgs) => {
  const { t } = useTranslation();

  return useMemo(() => {
    const typeLabels = getPartnerTypeLabels(customFields);
    const columnDefs: ColumnDef<PartnerData, any>[] = [
      {
        accessorKey: PartnerColumn.NAME,
        header: t(`partners.${PartnerColumn.NAME}`),
        cell: (info) => (
          <TableNameCell info={info} entityType={EntityType.PARTNER} />
        )
      },
      {
        accessorKey: PartnerColumn.TYPE,
        header: t(`partners.${PartnerColumn.TYPE}`),
        enableSorting: false,
        cell: (info) => {
          const typeValues = parseMultiSelectValue(info.getValue());
          if (typeValues.length === 0) {
            return t("tables.notAvailable");
          }
          // Labels arrive with the fields response; until then nothing can be named.
          if (!fieldsLoaded) {
            return null;
          }
          const labels = typeValues.map(
            (value) => typeLabels[String(value)] || t("notFound")
          );
          return <MultiSelectBadgesCell values={labels} />;
        }
      },
      {
        accessorKey: PartnerColumn.STATUS,
        header: t(`partners.${PartnerColumn.STATUS}`),
        sortingFn: createEnumSortingFn(PartnerStatus),
        minSize: 200,
        cell: (info) => {
          const statusKey = info.getValue() as PartnerStatus;
          const valueKey = getEnumTKey(
            "partners",
            "statusValues",
            PartnerStatus,
            statusKey
          );
          return (
            <StatusBadge statusColor={partnerStatusColors[statusKey]}>
              {t(valueKey)}
            </StatusBadge>
          );
        }
      },
      {
        accessorKey: PartnerColumn.PARENT_PARTNER,
        header: t(`partners.${PartnerColumn.PARENT_PARTNER}`),
        cell: (info) => {
          const parentPartnerId = info.getValue() as string | null | undefined;
          if (!parentPartnerId) {
            return t("tables.notAvailable");
          }
          return partnersDataMap[parentPartnerId]?.name || t("notFound");
        },
        enableSorting: false
      },
      {
        accessorKey: PartnerColumn.WEBSITE,
        header: t(`partners.${PartnerColumn.WEBSITE}`),
        cell: (info) => info.getValue() || t("tables.notAvailable")
      },
      {
        id: PartnerColumn.PHONE,
        accessorFn: (row) => row.phoneNumber,
        header: t(`partners.${PartnerColumn.PHONE}`),
        cell: (info) => info.getValue() || t("tables.notAvailable")
      },
      {
        accessorKey: PartnerColumn.COUNTRY,
        header: t(`partners.${PartnerColumn.COUNTRY}`),
        cell: (info) => {
          const country = info.getValue() as string | null;
          return country
            ? getCountryDisplayName(country, getCurrentLocale())
            : t("tables.notAvailable");
        }
      },
      {
        accessorKey: PartnerColumn.STATE_REGION,
        header: t(`partners.${PartnerColumn.STATE_REGION}`),
        cell: (info) => info.getValue() || t("tables.notAvailable")
      },
      {
        accessorKey: PartnerColumn.CITY,
        header: t(`partners.${PartnerColumn.CITY}`),
        cell: (info) => info.getValue() || t("tables.notAvailable")
      },
      {
        accessorKey: PartnerColumn.POSTAL_CODE,
        header: t(`partners.${PartnerColumn.POSTAL_CODE}`),
        cell: (info) => info.getValue() || t("tables.notAvailable")
      },
      {
        accessorKey: PartnerColumn.ADDRESS,
        header: t(`partners.${PartnerColumn.ADDRESS}`),
        cell: (info) => info.getValue() || t("tables.notAvailable")
      },
      {
        accessorKey: PartnerColumn.AREA_OF_RESPONSIBILITY,
        header: t(`partners.${PartnerColumn.AREA_OF_RESPONSIBILITY}`),
        cell: (info) => info.getValue() || t("tables.notAvailable")
      },
      {
        accessorKey: PartnerColumn.CREATED_AT,
        header: t(`tables.${PartnerColumn.CREATED_AT}`),
        sortingFn: "datetime",
        meta: { groupByMonth: true, localDay: true },
        cell: (info) =>
          info.getValue()
            ? simpleDate(toCalendarDate(info.getValue() as string), "short")
            : t("tables.notAvailable")
      },
      ...mapSelectedCustomColumnsToTableColumns(
        selectedColumns,
        customFields,
        t,
        HARDCODED_PARTNER_COLUMN_IDS
      )
    ];
    return !fieldsLoaded
      ? columnDefs
      : columnDefs.filter((col) => {
          const key = (
            "accessorKey" in col ? col.accessorKey : col.id
          ) as string;
          return isFieldVisible(key);
        });
  }, [
    partnersDataMap,
    selectedColumns,
    customFields,
    isFieldVisible,
    fieldsLoaded,
    t
  ]);
};
